import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Avatar, Flex } from 'antd';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Dropdown, type DropdownProps } from '@/components/AntdCompatDropdown';
import { NotificationIcon } from '../../assets/icons/icons';
import {
  NotificationGroup,
  NotificationUnreadCounts,
  notificationMarkAsRead,
  ShipmentMessage,
} from '../../apis/shipment';
import { useToken } from '../../common/helper';
import useNotificationsSocket, {
  NotificationData,
} from '../../context/useNotificationsSocket';
import useUnauthorize from '../../hooks/useUnauthorize';
import { setShipmentNav } from '@/redux-toolkit/slice/shipmentNavigationSlice';
import NotificationListSkeleton from './partials/NotificationListSkeleton';
import NotificationList, {
  isMentionMessage,
} from './partials/NotificationList';
import {
  type NotificationFilter,
  useRefreshUpdatesInbox,
  useUpdatesInbox,
} from './hooks/useUpdatesInbox';
import type { ErrorLike } from '@/apis/types';

const EMPTY_UNREAD_COUNTS: NotificationUnreadCounts = {
  all: 0,
  total: 0,
  mentions: 0,
};

interface NotificationDropdownProps {
  triggerNode?: React.ReactElement;
  placement?: DropdownProps['placement'];
  overlayStyle?: DropdownProps['overlayStyle'];
  overlayClassName?: DropdownProps['overlayClassName'];
  contentOnly?: boolean;
  headerExtra?: React.ReactNode;
  useGlobalSpin?: boolean;
  showSpin?: () => void;
  hideSpin?: () => void;
  open?: boolean;
  onAction?: () => void;
  onOpenChange?: (open: boolean) => void;
}

const patchMessageReadState = (
  groups: NotificationGroup[],
  messageId: string | null,
  markAll: boolean
): NotificationGroup[] =>
  groups.map((group) => {
    let unreadDelta = 0;
    const messages = group.messages.map((message) => {
      const shouldMarkRead = markAll || message.message_id === messageId;
      if (!shouldMarkRead || message.is_read) return message;

      unreadDelta += 1;
      return { ...message, is_read: true };
    });

    return {
      ...group,
      messages,
      unread_count: Math.max(0, (group.unread_count || 0) - unreadDelta),
    };
  });

const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
  triggerNode,
  placement = 'bottomRight',
  overlayStyle,
  overlayClassName,
  contentOnly = false,
  headerExtra,
  useGlobalSpin = false,
  showSpin,
  hideSpin,
  open,
  onAction,
  onOpenChange,
}) => {
  const { token } = useToken();
  const { unauthorize } = useUnauthorize();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [allNotifications, setAllNotifications] = useState<NotificationGroup[]>(
    []
  );
  const [unreadCounts, setUnreadCounts] = useState<NotificationUnreadCounts>({
    ...EMPTY_UNREAD_COUNTS,
  });
  const [activeFilter, setActiveFilter] = useState<NotificationFilter>('all');
  const [visible, setVisible] = useState<boolean>(false);
  const updatesInboxQuery = useUpdatesInbox({
    token,
    filter: activeFilter,
  });
  const refreshUpdatesInbox = useRefreshUpdatesInbox();
  const routeSignature = `${location.pathname}${location.search}`;
  const previousRouteSignatureRef = useRef(routeSignature);

  const handleNewNotification = useCallback(
    (newNotification: NotificationData) => {
      const message = newNotification?.data as ShipmentMessage;
      if (!message) return;

      setAllNotifications((prev) => {
        const updated = prev.map((group) => ({ ...group }));
        const existingGroup = updated.find((group) => group.date === 'Today');

        if (existingGroup) {
          existingGroup.messages = [message, ...existingGroup.messages];
          existingGroup.unread_count += message.is_read ? 0 : 1;
        } else {
          updated.unshift({
            date: 'Today',
            messages: [message],
            unread_count: message.is_read ? 0 : 1,
          });
        }

        return updated;
      });

      if (!message.is_read) {
        setUnreadCounts((prev) => ({
          all: prev.all + 1,
          total: prev.total + 1,
          mentions: prev.mentions + (isMentionMessage(message) ? 1 : 0),
        }));
      }
    },
    []
  );

  useNotificationsSocket(handleNewNotification);

  useEffect(() => {
    if (previousRouteSignatureRef.current === routeSignature) return;

    previousRouteSignatureRef.current = routeSignature;
    refreshUpdatesInbox();
  }, [refreshUpdatesInbox, routeSignature]);

  useEffect(() => {
    if (!updatesInboxQuery.data) return;

    setAllNotifications(updatesInboxQuery.data.groups);
    setUnreadCounts(updatesInboxQuery.data.unreadCounts);
  }, [updatesInboxQuery.data]);

  useEffect(() => {
    if (updatesInboxQuery.error) {
      unauthorize(updatesInboxQuery.error as ErrorLike);
    }
  }, [unauthorize, updatesInboxQuery.error]);

  useEffect(() => {
    if (!useGlobalSpin) return;

    if (updatesInboxQuery.isFetching) {
      showSpin?.();
      return;
    }

    hideSpin?.();
  }, [hideSpin, showSpin, updatesInboxQuery.isFetching, useGlobalSpin]);

  const computedUnreadCounts = useMemo(() => {
    return {
      all: unreadCounts.all || 0,
      mentions: unreadCounts.mentions || 0,
    };
  }, [unreadCounts]);

  const visibleNotifications = useMemo(
    () => allNotifications.filter((group) => group.messages.length > 0),
    [allNotifications]
  );

  const isOpen = open ?? visible;

  const handleOpenChange = (nextOpen: boolean) => {
    if (open === undefined) {
      setVisible(nextOpen);
    }
    onOpenChange?.(nextOpen);
  };

  const markSpecificAsRead = (id: string | null, markAs: boolean): void => {
    const previousNotifications = allNotifications;
    const previousUnreadCounts = unreadCounts;
    const targetMessages = allNotifications.flatMap((group) =>
      group.messages.filter(
        (message) => !message.is_read && (markAs || message.message_id === id)
      )
    );
    const readCount = targetMessages.length;
    const readMentionCount = targetMessages.filter(isMentionMessage).length;

    if (readCount > 0) {
      setAllNotifications((prev) => patchMessageReadState(prev, id, markAs));
      setUnreadCounts((prev) => ({
        all: Math.max(0, prev.all - readCount),
        total: Math.max(0, prev.total - readCount),
        mentions: Math.max(0, prev.mentions - readMentionCount),
      }));
    }

    notificationMarkAsRead(token, {
      message_ids: [id],
      mark_all: markAs,
    })
      .then(() => {
        refreshUpdatesInbox();
      })
      .catch((err) => {
        setAllNotifications(previousNotifications);
        setUnreadCounts(previousUnreadCounts);
        unauthorize(err);
      });
  };

  const goToShipmentComment = (message: ShipmentMessage) => {
    const shipmentId = message?.shipment?.id;
    if (!shipmentId) return;

    dispatch(
      setShipmentNav({
        drawerState: 'open',
        shipmentId,
        CoC_ID: message?.referenced_object?.id,
      })
    );
    navigate('/shipment');
  };

  const handleNotificationClick = (message: ShipmentMessage) => {
    goToShipmentComment(message);
    handleOpenChange(false);
    onAction?.();

    if (!message.is_read) {
      markSpecificAsRead(message.message_id, false);
    }
  };

  const dropdownContent = (
    <div className='notification-dropdown-wrapper'>
      <Flex className='notification-list-header' vertical gap={8}>
        <Flex align='center' justify='space-between'>
          <p className='notification-title'>Updates</p>

          <Flex align='center' gap={12}>
            <button
              type='button'
              onClick={() => {
                if (computedUnreadCounts.all > 0) {
                  markSpecificAsRead(null, true);
                  onAction?.();
                }
              }}
              className='notification-mark-read'
            >
              Mark all as read ({computedUnreadCounts.all})
            </button>
            {headerExtra}
          </Flex>
        </Flex>

        <Flex align='center' gap={4}>
          <button
            type='button'
            className={`notification-filter-pill ${
              activeFilter === 'all' ? 'active' : ''
            }`}
            onClick={() => setActiveFilter('all')}
          >
            <span>All</span>
            {computedUnreadCounts.all > 0 && (
              <span>{computedUnreadCounts.all}</span>
            )}
          </button>
          <button
            type='button'
            className={`notification-filter-pill ${
              activeFilter === 'mentions' ? 'active' : ''
            }`}
            onClick={() => setActiveFilter('mentions')}
          >
            <span>Mentions</span>
            {computedUnreadCounts.mentions > 0 && (
              <span>{computedUnreadCounts.mentions}</span>
            )}
          </button>
        </Flex>
      </Flex>

      {updatesInboxQuery.isFetching ? (
        <NotificationListSkeleton />
      ) : visibleNotifications?.length > 0 ? (
        <NotificationList
          groups={visibleNotifications}
          onNotificationClick={handleNotificationClick}
        />
      ) : (
        <div className='notification-empty-state'>
          <p className='flex-center x disabled-row'>No notifications</p>
        </div>
      )}
    </div>
  );

  if (contentOnly) {
    return dropdownContent;
  }

  return (
    <div style={{ position: 'relative' }}>
      {computedUnreadCounts.all > 0 && <div className='unread-dot' />}

      <Dropdown
        overlay={dropdownContent}
        trigger={['click']}
        open={isOpen}
        onOpenChange={handleOpenChange}
        placement={placement}
        overlayStyle={overlayStyle}
        overlayClassName={overlayClassName}
      >
        {triggerNode ?? (
          <Avatar
            size={36}
            icon={<NotificationIcon />}
            style={{ cursor: 'pointer', backgroundColor: '#000' }}
          />
        )}
      </Dropdown>
    </div>
  );
};

export default NotificationDropdown;
